import { RefObject, useMemo, useRef, useState } from "react";
import { IListRef, ListScrollbox } from "~/components/list";
import { Helmet } from "react-helmet-async";
import { useTopScrollShadow } from "~/hooks/useScrollShadow";
import { ICommandArgs, useRegisterCommands } from "~/environment/command.service";
import { RemindMeDialogState } from "~/dialogs/remind-me";
import {
  ESCAPE_TO_INBOX_COMMAND,
  markDoneCommand,
  markNotDoneCommand,
  setThreadReminderCommand,
  removeThreadReminderCommand,
  starThreadCommand,
  unstarThreadCommand,
  markDoneAndIgnoreRequestsCommand,
  markDoneAndCompleteRequestsCommand,
  markAllThreadRequestsCompleteCommand,
} from "~/utils/common-commands";
import * as MainLayout from "~/page-layouts/main-layout";
import { ContentList, EmptyListMessage, useKBarAwareFocusedEntry$ } from "~/components/content-list/ContentList";
import { triageThread } from "~/actions/notification";
import { useClientEnvironment } from "~/environment/ClientEnvironmentContext";
import {
  MarkAllDoneEntryAction,
  MarkAllNotDoneEntryAction,
  OtherCommandEntryAction,
  SetReminderForAllEntryAction,
} from "~/components/content-list/layout";
import { useRegisterBulkRecordActionCommands } from "~/hooks/useRegisterBulkEntryActions";
import { ParentComponent } from "~/utils/type-helpers";
import { useVirtualList } from "~/hooks/useVirtualList";
import { onRequestsEntrySelect, TRequestsEntry, useRequestsViewThreadContext } from "./utils";
import { ThreadViewContextProvider } from "../thread/context";
import { Outlet } from "@tanstack/react-router";
import { useIsRouteActive } from "~/environment/router/components";
import { cx } from "@emotion/css";
import { EndOfListMsg, LoadingMoreListEntriesMsg } from "~/components/EndOfListMsg";
import { useRegisterThreadLabelCommands } from "~/hooks/useRegisterThreadLabelCommands";
import { KBarState } from "~/dialogs/kbar";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { useRequestViewThreads } from "~/hooks/useRequestViewThreads";
import { ThreadEntry } from "~/components/content-list/ThreadEntry";

export const RequestsView: ParentComponent<{}> = () => {
  const environment = useClientEnvironment();
  const scrollboxRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<IListRef<TRequestsEntry>>(null);
  const threadViewContext = useRequestsViewThreadContext({ listRef });
  const isThreadOpen = useIsRouteActive({ path: "/requests/threads/$threadId" });
  const [filterBy, setFilterBy] = useState<"received" | "sent">("received");

  const [threads, { isLoading, fetchMore, nextId }] = useRequestViewThreads({ filterBy });

  const hasNextPage = !!nextId;

  const virtualThreadIds = useVirtualList({
    scrollboxRef,
    count: threads.length,
    getEntryKey: (index) => threads[index]?.id || "",
    fetchMore,
    hasNextPage,
    isFetchingNextPage: isLoading,
  });

  const threadIds = useMemo(() => threads.map((t) => t.id), [threads]);
  const threadCount = virtualThreadIds.entries.length;
  const showNoRequestsMessage = threadCount === 0 && !isLoading;

  const { setFocusedEntry, useFocusedEntry } = useKBarAwareFocusedEntry$<TRequestsEntry>();

  useTopScrollShadow({
    scrollboxRef,
    targetRef: headerRef,
  });

  return (
    <ListScrollbox ref={scrollboxRef}>
      <div className="h-screen overflow-auto">
        <RegisterReminderNotificationEntryCommands
          listRef={listRef}
          isListRefSet={!showNoRequestsMessage}
          useFocusedEntry={useFocusedEntry}
        />

        <Helmet>
          <title>Requests | Comms</title>
        </Helmet>

        <div ref={headerRef} className={cx("sticky top-0 z-[20]", isThreadOpen && "invisible")}>
          <MainLayout.Header ref={headerRef} className={cx("flex-col", isThreadOpen && "invisible")}>
            <div className="flex items-center">
              <h1 className="text-3xl">Requests</h1>
            </div>

            <MainLayout.HeaderMenu>
              <li className="flex items-center">
                <ToggleGroup.Root
                  type="single"
                  onValueChange={(e) => setFilterBy(e as "received" | "sent")}
                  onKeyDown={(e) => {
                    const isLeftOrRightArrowKey = e.key === "ArrowLeft" || e.key === "ArrowRight";
                    if (!isLeftOrRightArrowKey) return;
                    // If we don't do this, then ArrowLeft will focus the sidebar
                    e.stopPropagation();
                  }}
                >
                  <ToggleItem value="received" isSelected={filterBy === "received"}>
                    <small>Received</small>
                  </ToggleItem>

                  <ToggleItem value="sent" isSelected={filterBy === "sent"}>
                    <small>Sent</small>
                  </ToggleItem>
                </ToggleGroup.Root>
              </li>
            </MainLayout.HeaderMenu>
          </MainLayout.Header>

          <MainLayout.ActionsBar
            listRef={listRef}
            isListRefSet={threadCount > 0}
            multiSelectActions={
              <>
                <MarkAllDoneEntryAction />
                <MarkAllNotDoneEntryAction />
                <SetReminderForAllEntryAction />
                <OtherCommandEntryAction />
              </>
            }
            className={cx(isThreadOpen && "invisible")}
          />
        </div>

        {showNoRequestsMessage ?
          <NoRequestssMessage className={cx(isThreadOpen && "invisible")} />
        : <ContentList<TRequestsEntry>
            listRef={listRef}
            mode={isThreadOpen ? "active-descendent" : "focus"}
            onEntryFocused={setFocusedEntry}
            onEntryAction={(event) => onRequestsEntrySelect(environment, { event })}
            className={cx(isThreadOpen && "invisible")}
            autoFocus
            allEntryIdsForVirtualizedList={threadIds}
            style={virtualThreadIds.containerStyles()}
          >
            {virtualThreadIds.entries.map((virtualEntry) => {
              const threadId = virtualEntry.key as string;
              if (!threadId) return null;

              return (
                <ThreadEntry
                  key={threadId}
                  threadId={threadId}
                  showRequestDueDate
                  relativeOrder={virtualEntry.index}
                  style={virtualThreadIds.entryStyles(virtualEntry)}
                />
              );
            })}
          </ContentList>
        }

        {threadCount > 0 && !hasNextPage && !isLoading && <EndOfListMsg className={cx(isThreadOpen && "invisible")} />}

        {(hasNextPage || isLoading) && <LoadingMoreListEntriesMsg isThreadOpen={isThreadOpen} />}

        <ThreadViewContextProvider context={threadViewContext}>
          <Outlet />
        </ThreadViewContextProvider>
      </div>
    </ListScrollbox>
  );
};

/* -----------------------------------------------------------------------------------------------*/

const NoRequestssMessage: ParentComponent<{
  className?: string;
}> = (props) => <EmptyListMessage text="None." className={props.className} />;

const RegisterReminderNotificationEntryCommands: ParentComponent<{
  listRef: RefObject<IListRef<TRequestsEntry>>;
  isListRefSet: boolean;
  useFocusedEntry: () => TRequestsEntry | null;
}> = (props) => {
  const { listRef, isListRefSet, useFocusedEntry } = props;
  const focusedThread = useFocusedEntry();
  const environment = useClientEnvironment();

  useRegisterBulkRecordActionCommands({
    priority: { delta: 1 },
    listRef,
    isListRefSet,
  });

  useRegisterCommands({
    commands: () => {
      const commands: ICommandArgs[] = [ESCAPE_TO_INBOX_COMMAND];

      if (focusedThread) {
        commands.push(
          markDoneCommand({
            label: "Mark done...",
            closeKBarOnSelect: false,
            async callback() {
              KBarState.open({
                path: ["Mark done"],
                mode: "hotkey",
              });
            },
          }),
          markDoneAndIgnoreRequestsCommand({
            callback: () => {
              triageThread(environment, {
                threadId: focusedThread.record.id,
                done: true,
              });
            },
          }),
          markDoneAndCompleteRequestsCommand({
            callback: () => {
              triageThread(environment, {
                threadId: focusedThread.record.id,
                done: true,
                resolveRequests: true,
              });
            },
          }),
          markAllThreadRequestsCompleteCommand({
            callback: () => {
              triageThread(environment, {
                threadId: focusedThread.record.id,
                resolveRequests: true,
              });
            },
          }),
          markNotDoneCommand({
            callback: () => {
              triageThread(environment, {
                threadId: focusedThread.record.id,
                done: false,
              });
            },
          }),
          setThreadReminderCommand({
            callback: () => {
              RemindMeDialogState.open({
                threadId: focusedThread.record.id,
                fetchStrategy: environment.recordLoader.options.defaultFetchStrategy,
              });
            },
          }),
          removeThreadReminderCommand({
            callback: () => {
              triageThread(environment, {
                threadId: focusedThread.id,
                remindAt: null,
              });
            },
          }),
          starThreadCommand({
            callback: () => {
              triageThread(environment, {
                threadId: focusedThread.record.id,
                isStarred: true,
              });
            },
          }),
          unstarThreadCommand({
            callback: () => {
              triageThread(environment, {
                threadId: focusedThread.record.id,
                isStarred: false,
              });
            },
          }),
        );
      }

      return commands;
    },
    deps: [focusedThread, environment],
  });

  useRegisterThreadLabelCommands({
    threadId: focusedThread?.record.id,
  });

  return null;
};

/* -----------------------------------------------------------------------------------------------*/

const ToggleItem: ParentComponent<{ value: string; isSelected: boolean }> = (props) => {
  return (
    <ToggleGroup.Item
      value={props.value}
      className={cx(
        "inline-flex items-center border group px-2 py-1",
        "first:rounded-l last:rounded-r",
        "border-r-0 last:border-r",
        "border-slate-9",

        props.isSelected ? "bg-slate-12 text-white" : "hover:bg-slateA-3",
      )}
    >
      {props.children}
    </ToggleGroup.Item>
  );
};

/* -----------------------------------------------------------------------------------------------*/
